/**
 * ADHAN NOTIFICATION SERVICE
 * ==========================
 * Schedules and manages prayer time notifications using Capacitor Local Notifications.
 * 
 * Features:
 * - Schedule notifications for all 5 daily prayers
 * - Per-prayer enable/disable
 * - Adhan sound options: Silent, Short (30s), Full
 * - Notification before prayer time (customizable offset)
 * - Reschedules daily at midnight
 */

import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';

// Storage keys
const STORAGE_KEYS = {
  ADHAN_SETTINGS: 'tanzeen_adhan_settings',
  LAST_SCHEDULED_DATE: 'tanzeen_adhan_last_scheduled',
};

// Adhan sound options
export const ADHAN_SOUND_OPTIONS = {
  silent: { id: 'silent', label: 'Silent', description: 'Vibrate only', duration: 0 },
  short: { id: 'short', label: '30 Seconds', description: 'Short Adhan clip', duration: 30 },
  full: { id: 'full', label: 'Full Adhan', description: 'Complete Adhan call', duration: 180 },
};

// Default settings
const DEFAULT_SETTINGS = {
  enabled: false,           // Master toggle
  adhanSound: 'short',      // 'silent', 'short', or 'full'
  notifyBefore: 0,          // Minutes before prayer time (0 = at prayer time)
  prayers: {
    fajr: true,
    dhuhr: true,
    asr: true,
    maghrib: true,
    isha: true,
  },
};

// Notification IDs for each prayer (fixed IDs for easy management)
// Day-offset scheme: base ID + (dayOffset * 10). Today's Fajr = 1001, tomorrow's Fajr = 1011, etc.
// Rolling 7-day schedule uses offsets 0..6, so IDs 1001-1055 are reserved for prayers.
const NOTIFICATION_IDS = {
  fajr: 1001,
  dhuhr: 1002,
  asr: 1003,
  maghrib: 1004,
  isha: 1005,
};
const ROLLING_DAYS = 7;
const idFor = (prayerKey, dayOffset = 0) => NOTIFICATION_IDS[prayerKey] + dayOffset * 10;

// Notification channels — one per sound option because Android channels
// are IMMUTABLE after creation (API 26+). If we used a single channel the
// sound baked in at first creation would win forever, ignoring per-notification
// `sound:` values. Bumping the suffix version forces a fresh channel with the
// new sound if we ever change the audio.
const CHANNEL_VERSION = 'v2';
const ADHAN_CHANNELS = {
  silent: `adhan_channel_silent_${CHANNEL_VERSION}`,
  short: `adhan_channel_short_${CHANNEL_VERSION}`,
  full: `adhan_channel_full_${CHANNEL_VERSION}`,
};

// Prayer display names
export const PRAYER_NAMES = {
  fajr: 'Fajr',
  dhuhr: 'Dhuhr',
  asr: 'Asr',
  maghrib: 'Maghrib',
  isha: 'Isha',
};

// Prayer emojis for notifications
export const PRAYER_EMOJIS = {
  fajr: '🌅',
  dhuhr: '☀️',
  asr: '🌤️',
  maghrib: '🌅',
  isha: '🌙',
};

/**
 * Check if running on native platform
 */
const isNativeApp = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

/**
 * Get current adhan settings from storage
 */
export const getAdhanSettings = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.ADHAN_SETTINGS);
    if (saved) {
      return { ...DEFAULT_SETTINGS, ...JSON.parse(saved) };
    }
  } catch (e) {
    console.error('[Adhan] Failed to load settings:', e);
  }
  return DEFAULT_SETTINGS;
};

/**
 * Save adhan settings to storage
 */
export const saveAdhanSettings = (settings) => {
  try {
    localStorage.setItem(STORAGE_KEYS.ADHAN_SETTINGS, JSON.stringify(settings));
    return true;
  } catch (e) {
    console.error('[Adhan] Failed to save settings:', e);
    return false;
  }
};

/**
 * Request notification permission
 */
export const requestNotificationPermission = async () => {
  if (!isNativeApp()) {
    // Web fallback - use browser Notification API
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return false;
  }

  try {
    const result = await LocalNotifications.requestPermissions();
    return result.display === 'granted';
  } catch (e) {
    console.error('[Adhan] Failed to request permission:', e);
    return false;
  }
};

/**
 * Check if notifications are enabled
 */
export const checkNotificationPermission = async () => {
  if (!isNativeApp()) {
    if ('Notification' in window) {
      return Notification.permission === 'granted';
    }
    return false;
  }

  try {
    const result = await LocalNotifications.checkPermissions();
    return result.display === 'granted';
  } catch (e) {
    console.error('[Adhan] Failed to check permission:', e);
    return false;
  }
};

/**
 * Check whether Android has granted the optional special access needed for
 * precise, Doze-resilient prayer reminders. Reminders remain available with
 * inexact scheduling when this access is unavailable or declined.
 */
export const checkExactAlarmAccess = async () => {
  if (!isNativeApp()) {
    return { supported: false, granted: false, status: 'not_native' };
  }

  try {
    const result = await LocalNotifications.checkExactNotificationSetting();
    const status = result?.exact_alarm || 'denied';
    return { supported: true, granted: status === 'granted', status };
  } catch (e) {
    console.warn('[Adhan] Unable to check exact alarm access:', e);
    return { supported: true, granted: false, status: 'unavailable' };
  }
};

/**
 * Open Android's special-access screen only after the user explicitly elects
 * precise timing from Settings, then return the current status.
 */
export const requestExactAlarmAccess = async () => {
  const current = await checkExactAlarmAccess();
  if (!current.supported || current.granted) return current;

  try {
    await LocalNotifications.changeExactNotificationSetting();
  } catch (e) {
    console.warn('[Adhan] Exact alarm settings were not opened:', e);
  }

  return checkExactAlarmAccess();
};

/**
 * Parse time string (HH:MM) to Date object for today or a future day.
 * @param {string} timeStr - "HH:MM"
 * @param {number} offsetMinutes - subtract this many minutes (for "notify before")
 * @param {number} dayOffset - 0 = today, 1 = tomorrow, ...
 */
const parseTimeToDate = (timeStr, offsetMinutes = 0, dayOffset = 0) => {
  if (!timeStr || timeStr === '--:--') return null;
  
  const [hours, minutes] = timeStr.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return null;
  
  const date = new Date();
  if (dayOffset > 0) date.setDate(date.getDate() + dayOffset);
  date.setHours(hours, minutes - offsetMinutes, 0, 0);
  
  return date;
};

/**
 * Schedule a single prayer notification for a specific day offset.
 * @param {string} prayerKey - 'fajr' | 'dhuhr' | 'asr' | 'maghrib' | 'isha'
 * @param {string} prayerTime - "HH:MM"
 * @param {object} settings - adhan settings
 * @param {number} dayOffset - 0 = today, 1 = tomorrow, ...
 */
const schedulePrayerNotification = async (prayerKey, prayerTime, settings, dayOffset = 0, exactAlarmGranted = false) => {
  const notificationId = idFor(prayerKey, dayOffset);
  const prayerName = PRAYER_NAMES[prayerKey];
  const emoji = PRAYER_EMOJIS[prayerKey];
  
  // Parse time to Date
  const notifyAt = parseTimeToDate(prayerTime, settings.notifyBefore, dayOffset);
  if (!notifyAt) {
    console.log(`[Adhan] Skipping ${prayerName} (day+${dayOffset}) - invalid time: ${prayerTime}`);
    return { scheduled: false, exact: false };
  }
  
  // Skip if time has already passed
  const now = new Date();
  if (notifyAt <= now) {
    if (dayOffset === 0) {
      console.log(`[Adhan] Skipping ${prayerName} (today) - time already passed`);
    }
    return { scheduled: false, exact: false };
  }
  
  try {
    // Cancel existing notification at this slot
    await LocalNotifications.cancel({ notifications: [{ id: notificationId }] });
    
    // Sound file lives at res/raw/adhan_short.mp3 or res/raw/adhan_full.mp3.
    // If missing at build time, Android will fall back to the channel's default
    // sound (the OS notification tone) — safe but not the full Adhan.
    let soundFile = null;
    let channelId = ADHAN_CHANNELS.silent;
    if (settings.adhanSound === 'short') {
      soundFile = 'adhan_short';
      channelId = ADHAN_CHANNELS.short;
    } else if (settings.adhanSound === 'full') {
      soundFile = 'adhan_full';
      channelId = ADHAN_CHANNELS.full;
    }
    
    const notification = {
      id: notificationId,
      title: `${emoji} ${prayerName} Time`,
      body: settings.notifyBefore > 0
        ? `${prayerName} prayer in ${settings.notifyBefore} minutes`
        : `It's time for ${prayerName} prayer`,
      schedule: {
        at: notifyAt,
        allowWhileIdle: true,   // wake from Doze
      },
      // Keep the fallback inexact until the user explicitly enables Android's
      // special exact-alarm access from Settings.
      isExactNotification: exactAlarmGranted,
      sound: soundFile,
      smallIcon: 'ic_notification',
      largeIcon: 'ic_launcher',
      channelId,
      extra: {
        prayer: prayerKey,
        type: 'adhan',
        adhanSound: settings.adhanSound,
        dayOffset,
      },
    };
    
    const result = await LocalNotifications.schedule({
      notifications: [notification],
    });
    
    console.log(`[Adhan] Scheduled ${prayerName} for ${notifyAt.toLocaleString()} (day+${dayOffset}, sound=${settings.adhanSound}, channel=${channelId})`);
    return { scheduled: true, exact: exactAlarmGranted && !result?.warning };
  } catch (e) {
    console.error(`[Adhan] Failed to schedule ${prayerName} (day+${dayOffset}):`, e);
    return { scheduled: false, exact: false };
  }
};

/**
 * Schedule all prayer notifications across a rolling ROLLING_DAYS window.
 * Uses TODAY's prayer times as a fallback for future days — accurate enough
 * to guarantee an Adhan fires even if the user never opens the app for a week.
 * Whenever the app is next opened, `checkAndReschedule` refreshes with the
 * exact times for that day, correcting any drift.
 */
export const scheduleAllPrayerNotifications = async (prayerTimes) => {
  if (!isNativeApp()) {
    console.log('[Adhan] Skipping schedule - not on native platform');
    return { scheduled: 0, skipped: 5 };
  }
  
  const settings = getAdhanSettings();
  
  if (!settings.enabled) {
    console.log('[Adhan] Notifications disabled in settings');
    return { scheduled: 0, skipped: 5, reason: 'disabled' };
  }
  
  // Ensure channels exist before scheduling (idempotent)
  await initializeNotificationChannel();
  
  // Check permission
  const hasPermission = await checkNotificationPermission();
  if (!hasPermission) {
    console.log('[Adhan] No notification permission');
    return { scheduled: 0, skipped: 5, reason: 'no_permission' };
  }

  // Exact timing is optional special access. Never let the plugin open the
  // system settings unexpectedly: denied/unavailable access schedules safely
  // with Android's inexact, Doze-aware fallback instead.
  const exactAlarm = await checkExactAlarmAccess();
  
  // Map prayer times to our format
  const times = {
    fajr: prayerTimes?.Fajr || prayerTimes?.fajr,
    dhuhr: prayerTimes?.Dhuhr || prayerTimes?.dhuhr,
    asr: prayerTimes?.Asr || prayerTimes?.asr,
    maghrib: prayerTimes?.Maghrib || prayerTimes?.maghrib,
    isha: prayerTimes?.Isha || prayerTimes?.isha,
  };
  
  let scheduled = 0;
  let skipped = 0;
  let exactScheduled = 0;
  let inexactScheduled = 0;
  
  // Rolling window: schedule today + next (ROLLING_DAYS - 1) days.
  for (let dayOffset = 0; dayOffset < ROLLING_DAYS; dayOffset++) {
    for (const [prayerKey, enabled] of Object.entries(settings.prayers)) {
      if (!enabled) { skipped++; continue; }
      const time = times[prayerKey];
      if (!time) { skipped++; continue; }
      const result = await schedulePrayerNotification(
        prayerKey,
        time,
        settings,
        dayOffset,
        exactAlarm.granted,
      );
      if (result.scheduled) {
        scheduled++;
        if (result.exact) exactScheduled++; else inexactScheduled++;
      } else {
        skipped++;
      }
    }
  }
  
  // Save last scheduled date
  localStorage.setItem(STORAGE_KEYS.LAST_SCHEDULED_DATE, new Date().toDateString());
  
  console.log(`[Adhan] Scheduled ${scheduled} notifications across ${ROLLING_DAYS}-day window, skipped ${skipped}, exact ${exactScheduled}, inexact ${inexactScheduled}`);
  return { scheduled, skipped, exactScheduled, inexactScheduled, exactAlarm };
};

/**
 * Cancel all prayer notifications across the rolling window
 */
export const cancelAllPrayerNotifications = async () => {
  if (!isNativeApp()) return;
  
  try {
    const ids = [];
    for (let dayOffset = 0; dayOffset < ROLLING_DAYS; dayOffset++) {
      for (const key of Object.keys(NOTIFICATION_IDS)) {
        ids.push({ id: idFor(key, dayOffset) });
      }
    }
    await LocalNotifications.cancel({ notifications: ids });
    console.log(`[Adhan] Cancelled ${ids.length} scheduled notifications`);
  } catch (e) {
    console.error('[Adhan] Failed to cancel notifications:', e);
  }
};

/**
 * Toggle notification for a specific prayer.
 * Cancels/schedules across the entire rolling window for that prayer.
 */
export const togglePrayerNotification = async (prayerKey, enabled, prayerTime) => {
  const settings = getAdhanSettings();
  settings.prayers[prayerKey] = enabled;
  saveAdhanSettings(settings);
  
  if (!isNativeApp()) return;
  
  if (enabled && prayerTime) {
    // Ensure channels exist, then re-schedule this prayer for all future days
    await initializeNotificationChannel();
    const exactAlarm = await checkExactAlarmAccess();
    for (let dayOffset = 0; dayOffset < ROLLING_DAYS; dayOffset++) {
      await schedulePrayerNotification(prayerKey, prayerTime, settings, dayOffset, exactAlarm.granted);
    }
  } else {
    const ids = [];
    for (let dayOffset = 0; dayOffset < ROLLING_DAYS; dayOffset++) {
      ids.push({ id: idFor(prayerKey, dayOffset) });
    }
    await LocalNotifications.cancel({ notifications: ids });
  }
};

/**
 * Enable/disable all notifications
 */
export const setNotificationsEnabled = async (enabled, prayerTimes) => {
  const settings = getAdhanSettings();
  settings.enabled = enabled;
  saveAdhanSettings(settings);
  
  if (enabled && prayerTimes) {
    return await scheduleAllPrayerNotifications(prayerTimes);
  } else {
    await cancelAllPrayerNotifications();
    return { scheduled: 0, skipped: 5, reason: 'disabled' };
  }
};

/**
 * Set silent mode
 */
export const setSilentMode = async (silent, prayerTimes) => {
  const settings = getAdhanSettings();
  settings.adhanSound = silent ? 'silent' : 'short';
  saveAdhanSettings(settings);
  
  // Reschedule with new sound settings
  if (settings.enabled && prayerTimes) {
    await scheduleAllPrayerNotifications(prayerTimes);
  }
};

/**
 * Set Adhan sound option
 */
export const setAdhanSound = async (soundOption, prayerTimes) => {
  const settings = getAdhanSettings();
  settings.adhanSound = soundOption; // 'silent', 'short', or 'full'
  saveAdhanSettings(settings);
  
  // Reschedule with new sound settings
  if (settings.enabled && prayerTimes) {
    await scheduleAllPrayerNotifications(prayerTimes);
  }
};

/**
 * Set notify before time (minutes before prayer)
 */
export const setNotifyBefore = async (minutes, prayerTimes) => {
  const settings = getAdhanSettings();
  settings.notifyBefore = minutes;
  saveAdhanSettings(settings);
  
  // Reschedule with new offset
  if (settings.enabled && prayerTimes) {
    await scheduleAllPrayerNotifications(prayerTimes);
  }
};

/**
 * Initialize notification channels (Android).
 * Creates THREE immutable channels — one per sound option. Channels are
 * versioned via CHANNEL_VERSION so we can force a fresh sound assignment
 * on future updates. Safe to call multiple times (idempotent).
 */
export const initializeNotificationChannel = async () => {
  if (!isNativeApp()) return;
  
  try {
    // Silent channel — vibrate + light only
    await LocalNotifications.createChannel({
      id: ADHAN_CHANNELS.silent,
      name: 'Prayer Reminders (Silent)',
      description: 'Prayer time notifications — vibration only',
      importance: 5,
      visibility: 1,
      sound: null,
      vibration: true,
      lights: true,
    });

    // Short Adhan channel — 30-second clip
    await LocalNotifications.createChannel({
      id: ADHAN_CHANNELS.short,
      name: 'Prayer Reminders (Short Adhan)',
      description: 'Prayer time notifications with a 30-second Adhan',
      importance: 5,
      visibility: 1,
      sound: 'adhan_short',
      vibration: true,
      lights: true,
    });

    // Full Adhan channel — complete call
    await LocalNotifications.createChannel({
      id: ADHAN_CHANNELS.full,
      name: 'Prayer Reminders (Full Adhan)',
      description: 'Prayer time notifications with the full Adhan',
      importance: 5,
      visibility: 1,
      sound: 'adhan_full',
      vibration: true,
      lights: true,
    });

    console.log('[Adhan] Notification channels created:', Object.values(ADHAN_CHANNELS));
  } catch (e) {
    console.error('[Adhan] Failed to create channels:', e);
  }
};

/**
 * App-boot initialiser. Call once from App.js on mount. Idempotent.
 * - Creates channels so the FIRST-ever notification has the right sound
 *   (channels are immutable → must exist before scheduling).
 * - Runs `checkAndReschedule` so notifications continue after a device
 *   reboot without waiting for the user to open Settings.
 * - Reads prayer times from the SAME localStorage keys the rest of the app
 *   writes to (`ustadSyncData`, `quranplus-extracted-source`) so this file
 *   never touches the prayer-time calculation stack.
 * - No-op on web.
 */
const readCachedPrayerTimes = () => {
  try {
    const savedSync = localStorage.getItem('ustadSyncData');
    if (savedSync) {
      const s = JSON.parse(savedSync);
      if (s?.startTimes) {
        return {
          Fajr: s.startTimes.fajr,
          Dhuhr: s.startTimes.dhuhr,
          Asr: s.startTimes.asr,
          Maghrib: s.startTimes.maghrib,
          Isha: s.startTimes.isha,
        };
      }
    }
    const savedExtracted = localStorage.getItem('quranplus-extracted-source');
    if (savedExtracted) {
      const c = JSON.parse(savedExtracted);
      if (c?.times) return c.times;
    }
  } catch (e) {
    console.warn('[Adhan] Failed to read cached prayer times:', e);
  }
  return null;
};

export const bootInitAdhan = async (prayerTimes) => {
  if (!isNativeApp()) return { skipped: true, reason: 'not_native' };
  
  try {
    await initializeNotificationChannel();
    const settings = getAdhanSettings();
    if (!settings.enabled) return { skipped: true, reason: 'disabled' };
    const times = prayerTimes || readCachedPrayerTimes();
    if (!times) return { skipped: true, reason: 'no_prayer_times_cached_yet' };
    return await checkAndReschedule(times);
  } catch (e) {
    console.error('[Adhan] bootInitAdhan failed:', e);
    return { skipped: true, reason: 'error', error: e.message };
  }
};

/**
 * Check if notifications need to be rescheduled today
 */
export const checkAndReschedule = async (prayerTimes) => {
  const lastScheduled = localStorage.getItem(STORAGE_KEYS.LAST_SCHEDULED_DATE);
  const today = new Date().toDateString();
  
  if (lastScheduled !== today) {
    console.log('[Adhan] New day detected, rescheduling notifications');
    return await scheduleAllPrayerNotifications(prayerTimes);
  }
  
  return null; // Already scheduled for today
};

/**
 * Get pending notifications
 */
export const getPendingNotifications = async () => {
  if (!isNativeApp()) return [];
  
  try {
    const result = await LocalNotifications.getPending();
    return result.notifications || [];
  } catch (e) {
    console.error('[Adhan] Failed to get pending:', e);
    return [];
  }
};

/**
 * Send a test notification immediately
 * Useful for users to verify notifications are working
 */
export const sendTestNotification = async () => {
  const settings = getAdhanSettings();
  const isSilent = settings.adhanSound === 'silent';
  
  // For web, use browser Notification API
  if (!isNativeApp()) {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        new Notification('🕌 Tanzeen+ Test', {
          body: isSilent 
            ? 'Silent mode - vibrate only'
            : `Adhan sound: ${ADHAN_SOUND_OPTIONS[settings.adhanSound]?.label || 'Default'}`,
          icon: '/logo192.png',
          silent: isSilent,
        });
        return { success: true, platform: 'web' };
      }
      return { success: false, reason: 'permission_denied' };
    }
    return { success: false, reason: 'not_supported' };
  }
  
  // For native, use Capacitor Local Notifications
  try {
    // Check permission first
    const hasPermission = await checkNotificationPermission();
    if (!hasPermission) {
      const granted = await requestNotificationPermission();
      if (!granted) {
        return { success: false, reason: 'permission_denied' };
      }
    }
    
    // Make sure channels exist (idempotent)
    await initializeNotificationChannel();
    
    // Determine sound file + matching channel
    let soundFile = null;
    let channelId = ADHAN_CHANNELS.silent;
    if (settings.adhanSound === 'short') {
      soundFile = 'adhan_short';
      channelId = ADHAN_CHANNELS.short;
    } else if (settings.adhanSound === 'full') {
      soundFile = 'adhan_full';
      channelId = ADHAN_CHANNELS.full;
    }
    
    // Send immediate notification
    await LocalNotifications.schedule({
      notifications: [{
        id: 9999, // Special ID for test notification
        title: '🕌 Tanzeen+ Test',
        body: isSilent 
          ? 'Silent mode is ON - vibrate only'
          : `Adhan: ${ADHAN_SOUND_OPTIONS[settings.adhanSound]?.label || 'Default'}`,
        schedule: {
          at: new Date(Date.now() + 1000), // 1 second from now
          allowWhileIdle: true,
        },
        // A one-second test does not need special access and must not trigger
        // an exact-alarm settings prompt.
        isExactNotification: false,
        sound: soundFile,
        smallIcon: 'ic_notification',
        largeIcon: 'ic_launcher',
        channelId,
        extra: {
          type: 'test',
          adhanSound: settings.adhanSound,
        },
      }],
    });
    
    return { success: true, platform: 'native', sound: settings.adhanSound };
  } catch (e) {
    console.error('[Adhan] Test notification failed:', e);
    return { success: false, reason: e.message };
  }
};

export default {
  getAdhanSettings,
  saveAdhanSettings,
  requestNotificationPermission,
  checkNotificationPermission,
  checkExactAlarmAccess,
  requestExactAlarmAccess,
  scheduleAllPrayerNotifications,
  cancelAllPrayerNotifications,
  togglePrayerNotification,
  setNotificationsEnabled,
  setSilentMode,
  setAdhanSound,
  setNotifyBefore,
  initializeNotificationChannel,
  bootInitAdhan,
  checkAndReschedule,
  getPendingNotifications,
  sendTestNotification,
  PRAYER_NAMES,
  PRAYER_EMOJIS,
  ADHAN_SOUND_OPTIONS,
  DEFAULT_SETTINGS,
};
